import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { readDocTextForPrompt } from "../teachingPlans/documentsApi";
import { resolveAgenda, TEXTUAL_EXTS } from "../teachingPlans/TeachingPlansPage";

import { useMyEmployees } from "../workbench/EmployeeSwitcher";

import {
  canManageSchool,
  useClasses,
  useCreateUnit,
  useUnitsFromSyllabus,
  useCurriculumUnits,
  useDeleteUnit,
  useKnowledgePoints,
  useMarkTaught,
  useOrgMe,
  useUnmarkTaught,
  useCreateKp,
  type KnowledgePoint,
  useUpdateUnit,
  type CurriculumUnit,
} from "@/api/school";
import { useAuth } from "@/auth/OidcProvider";

const XUEAN_STAGES = ["标学", "互学", "试学", "作业", "考试"] as const;

/** 教学进度 — Exam Engine P0 (spec §5.3 / R1). The exam-scope inference
 *  reads ONLY units marked 已讲完 here, so the mark action is one click. */
export default function CurriculumPage() {
  const { t } = useTranslation();
  const { state } = useAuth();
  const isAdmin = state.status === "authenticated" && state.user.isAdmin;
  const orgMe = useOrgMe();
  const canManage = isAdmin || canManageSchool(orgMe.data);
  const classes = useClasses(canManage ? undefined : { mine: true });
  const [classId, setClassId] = useState<string>("");
  const effectiveClassId = classId || classes.data?.[0]?.id;
  const activeClass = (classes.data ?? []).find((c) => c.id === effectiveClassId);
  // 声明顺序:先学科再单元(单元列表按学科过滤)。
  // 学科标签挂钩(Jarod 2026-08-14):单元的知识点候选跟老师选择的学科,
  // 多学科账号不再回落英语树。
  const { employees } = useMyEmployees();
  const [pickedSubject, setPickedSubject] = useState<string>("");
  const effectiveSubjectId = pickedSubject || (employees[0]?.subject_id ?? "");
  const units = useCurriculumUnits(effectiveClassId, undefined, effectiveSubjectId || undefined);
  const markTaught = useMarkTaught();
  const unmark = useUnmarkTaught();
  const del = useDeleteUnit();
  const [editing, setEditing] = useState<CurriculumUnit | "new" | null>(null);
  const [fromSyllabus, setFromSyllabus] = useState(false);

  const byWeek = useMemo(() => {
    const m = new Map<string, CurriculumUnit[]>();
    for (const u of units.data ?? []) {
      const key = u.week_no != null ? String(u.week_no) : "—";
      m.set(key, [...(m.get(key) ?? []), u]);
    }
    return [...m.entries()].sort((a, b) => {
      if (a[0] === "—") return 1;
      if (b[0] === "—") return -1;
      return Number(a[0]) - Number(b[0]);
    });
  }, [units.data]);

  const taughtCount = (units.data ?? []).filter((u) => u.status === "taught").length;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-5 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{t("curriculum.title")}</h1>
          <p className="text-xs text-slate-500 mt-1">{t("curriculum.hint")}</p>
        </div>
        <div className="flex gap-2">
          {/* 从大纲一键生成单元+知识点(2026-09-15):新校 0 单元 0 知识点时,
              考试蓝图根本用不起来;这里把学期大纲拆成教学进度骨架。 */}
          <button
            type="button"
            onClick={() => setFromSyllabus(true)}
            disabled={!effectiveClassId}
            className="px-3 py-1.5 text-sm font-medium rounded border border-violet-300 text-violet-700 bg-white hover:bg-violet-50 disabled:opacity-50"
          >
            {t("curriculum.fromSyllabus.button")}
          </button>
          <button
            type="button"
            onClick={() => setEditing("new")}
            disabled={!effectiveClassId}
            className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {t("curriculum.addUnit")}
          </button>
        </div>
      </header>
      {fromSyllabus && effectiveClassId && (
        <FromSyllabusModal
          classId={effectiveClassId}
          subjectId={effectiveSubjectId || undefined}
          subjectSlug={employees.find((e) => e.subject_id === effectiveSubjectId)?.slug}
          onClose={() => setFromSyllabus(false)}
        />
      )}

      <div className="mb-4 flex items-center gap-3">
        {classes.data && classes.data.length === 0 && (
          <span className="text-xs text-amber-600">{t("common.noClassAssigned")}</span>
        )}
        <select
          value={effectiveClassId ?? ""}
          onChange={(e) => setClassId(e.target.value)}
          className="rounded border border-slate-300 px-2 py-1.5 text-sm"
        >
          {(classes.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {employees.length > 1 && (
          <select
            value={effectiveSubjectId}
            onChange={(e) => setPickedSubject(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            {employees.map((emp: { subject_id: string; name: string }) => (
              <option key={emp.subject_id} value={emp.subject_id}>{emp.name}</option>
            ))}
          </select>
        )}
        {activeClass && <span className="text-xs text-slate-400">{activeClass.semester}</span>}
        {(units.data?.length ?? 0) > 0 && (
          <span className="text-xs text-slate-500">
            {t("curriculum.progress", { taught: taughtCount, total: units.data!.length })}
          </span>
        )}
      </div>

      {units.isLoading && <p className="text-sm text-slate-400">…</p>}
      {!units.isLoading && (units.data?.length ?? 0) === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400">
          {t("curriculum.empty")}
        </div>
      )}

      <div className="space-y-4">
        {byWeek.map(([week, list]) => (
          <div key={week}>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              {week === "—" ? t("curriculum.noWeek") : t("curriculum.week", { week })}
            </h3>
            <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              {list.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <div className="min-w-0 flex-1">
                    <span className={`font-medium ${u.status === "taught" ? "text-slate-500" : "text-slate-800"}`}>
                      {u.title}
                    </span>
                    {u.xuean_stage && (
                      <span className="ml-2 rounded bg-indigo-50 border border-indigo-200 text-indigo-600 text-[11px] px-1.5 py-0.5">
                        {u.xuean_stage}
                      </span>
                    )}
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {u.kps.map((k) => (
                        <span key={k.kp_id} className="rounded bg-slate-50 border border-slate-200 text-slate-500 text-[11px] px-1.5">
                          {k.name_zh}
                        </span>
                      ))}
                    </div>
                  </div>
                  {u.status === "taught" ? (
                    <>
                      <span className="text-[11px] text-emerald-600">
                        ✓ {t("curriculum.taughtOn", { date: u.actual_taught_date })}
                      </span>
                      <button
                        type="button"
                        disabled={unmark.isPending}
                        onClick={() => unmark.mutate(u.id)}
                        className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-400 hover:bg-slate-50"
                      >
                        {t("curriculum.unmark")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      disabled={markTaught.isPending}
                      onClick={() => markTaught.mutate(u.id)}
                      className="px-3 py-1.5 text-xs font-medium rounded bg-emerald-500 text-white hover:bg-emerald-600"
                    >
                      ✓ {t("curriculum.markTaught")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditing(u)}
                    className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    disabled={del.isPending}
                    onClick={() => {
                      if (window.confirm(t("curriculum.deleteConfirm"))) del.mutate(u.id);
                    }}
                    className="px-2 py-1 text-xs rounded border border-rose-200 text-rose-400 hover:bg-rose-50"
                  >
                    🗑
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && effectiveClassId && (
        <UnitModal
          unit={editing === "new" ? null : editing}
          classId={effectiveClassId}
          subjectId={effectiveSubjectId || undefined}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function FromSyllabusModal({ classId, subjectId, subjectSlug, onClose }: { classId: string; subjectId?: string; subjectSlug?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const run = useUnitsFromSyllabus();
  const [text, setText] = useState("");
  const [weeks, setWeeks] = useState(18);
  const [loadingAgenda, setLoadingAgenda] = useState(false);
  const [result, setResult] = useState<{ units_created: number; kps_created: string[]; kp_status: string } | null>(null);

  async function loadAgenda() {
    setLoadingAgenda(true);
    try {
      const found = await resolveAgenda(subjectSlug ?? "");
      if (found && TEXTUAL_EXTS.test(found.file.name)) {
        setText(await readDocTextForPrompt(found.path, found.file.name));
      } else {
        setText((cur) => cur || "");
        window.alert(t("curriculum.fromSyllabus.noAgenda"));
      }
    } finally {
      setLoadingAgenda(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="from-syllabus-modal">
        <h2 className="text-base font-semibold text-slate-800 mb-1">{t("curriculum.fromSyllabus.title")}</h2>
        <p className="text-xs text-slate-500 mb-3">{t("curriculum.fromSyllabus.hint")}</p>
        {!result ? (
          <>
            <div className="mb-2 flex items-center gap-2">
              <button type="button" onClick={() => void loadAgenda()} disabled={loadingAgenda} className="px-2.5 py-1 text-xs rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                {loadingAgenda ? t("common.loading") : t("curriculum.fromSyllabus.loadAgenda")}
              </button>
              <label className="ml-auto flex items-center gap-1 text-xs text-slate-600">
                {t("curriculum.fromSyllabus.weeks")}
                <input type="number" min={1} max={40} value={weeks} onChange={(e) => setWeeks(Number(e.target.value) || 18)} className="w-16 px-2 py-1 rounded border border-slate-200 text-sm" />
              </label>
            </div>
            <textarea
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("curriculum.fromSyllabus.textPlaceholder")}
              className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm font-mono"
            />
            {run.isError && <p className="mt-2 text-xs text-rose-600">{(run.error as Error).message}</p>}
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-200 text-slate-600 hover:bg-slate-50">{t("common.cancel")}</button>
              <button
                type="button"
                disabled={text.trim().length < 30 || run.isPending}
                onClick={() => run.mutate({ class_id: classId, subject_id: subjectId, text: text.trim(), weeks }, { onSuccess: (r) => setResult(r) })}
                className="px-3 py-1.5 text-sm font-medium rounded bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50"
              >
                {run.isPending ? t("curriculum.fromSyllabus.generating") : t("curriculum.fromSyllabus.generate")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              {t("curriculum.fromSyllabus.done", { units: result.units_created, kps: result.kps_created.length })}
              {" "}
              {result.kp_status === "candidate" ? t("curriculum.fromSyllabus.kpsCandidate") : t("curriculum.fromSyllabus.kpsApproved")}
            </p>
            {result.kps_created.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {result.kps_created.map((k) => <span key={k} className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700">{k}</span>)}
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600">{t("common.close", { defaultValue: "关闭" })}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function UnitModal({ unit, classId, subjectId, onClose }: { unit: CurriculumUnit | null; classId: string; subjectId?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateUnit();
  const update = useUpdateUnit();
  const kps = useKnowledgePoints("approved", subjectId);
  const [form, setForm] = useState({
    title: unit?.title ?? "",
    week_no: unit?.week_no != null ? String(unit.week_no) : "",
    xuean_stage: unit?.xuean_stage ?? "",
    kp_ids: new Set(unit?.kps.map((k) => k.kp_id) ?? []),
  });
  const busy = create.isPending || update.isPending;
  // 自定义知识点(Jarod 2026-08-14):老师现场输入 → 建为 candidate
  // (待管理员审核后才计入考试范围)并立刻选中。
  const createKp = useCreateKp();
  const [customKp, setCustomKp] = useState("");
  const [extraKps, setExtraKps] = useState<KnowledgePoint[]>([]);
  async function addCustomKp() {
    const name = customKp.trim();
    if (!name) return;
    const code = `CUS-${Date.now().toString(36).toUpperCase()}`;
    const created = (await createKp.mutateAsync({ code, name_zh: name, subject_id: subjectId || undefined })) as KnowledgePoint;
    setExtraKps((prev) => [...prev, created]);
    setForm((f) => ({ ...f, kp_ids: new Set([...f.kp_ids, created.id]) }));
    setCustomKp("");
  }
  // 可选 KP = 排除"能力轴":被引用为父级的节点,或 name_en 命中六技能
  // 轴名的根节点(与后端 item_scores 的 axis_roots 约定一致)。旧写法
  // (parent_id != null)会把数学等平铺学科滤成空;只按父级引用又会让
  // 无子节点的轴根(听力/口语/写作)漏进来 —— 两条都要。
  const AXIS_EN = new Set(["listening", "speaking", "reading", "writing", "vocabulary", "grammar"]);
  const parentIds = new Set((kps.data ?? []).map((k) => k.parent_id).filter(Boolean));
  const leafKps = [...(kps.data ?? []), ...extraKps].filter(
    (k) => !parentIds.has(k.id) && !(k.parent_id === null && AXIS_EN.has((k.name_en ?? "").toLowerCase())),
  );

  function save() {
    const body = {
      class_id: classId,
      subject_id: subjectId || undefined,
      title: form.title.trim(),
      week_no: form.week_no ? Number(form.week_no) : null,
      xuean_stage: form.xuean_stage || null,
      kp_ids: [...form.kp_ids],
    };
    if (unit) update.mutate({ id: unit.id, ...body }, { onSuccess: onClose });
    else create.mutate(body, { onSuccess: onClose });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">
          {unit ? t("curriculum.editUnit") : t("curriculum.addUnit")}
        </h2>
        <div className="space-y-2">
          <label className="block text-sm">
            <span className="text-slate-600 text-xs">{t("curriculum.fields.title")}</span>
            <input
              value={form.title}
              placeholder={t("curriculum.fields.titlePlaceholder")}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
            />
          </label>
          <div className="flex gap-2">
            <label className="block text-sm w-24">
              <span className="text-slate-600 text-xs">{t("curriculum.fields.week")}</span>
              <input
                type="number"
                min={1}
                max={30}
                value={form.week_no}
                onChange={(e) => setForm({ ...form, week_no: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-sm flex-1">
              <span className="text-slate-600 text-xs">{t("curriculum.fields.stage")}</span>
              <select
                value={form.xuean_stage}
                onChange={(e) => setForm({ ...form, xuean_stage: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              >
                <option value="">—</option>
                {XUEAN_STAGES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="text-sm">
            <span className="text-slate-600 text-xs">{t("curriculum.fields.kps")}</span>
            <span className="ml-1 text-[10px] text-slate-400">{t("curriculum.fields.customKpHint")}</span>
            <div className="mb-1.5 flex gap-1.5">
              <input
                value={customKp}
                onChange={(e) => setCustomKp(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void addCustomKp(); } }}
                placeholder={t("curriculum.fields.customKpPlaceholder")}
                className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
              />
              <button
                type="button"
                disabled={!customKp.trim() || createKp.isPending}
                onClick={() => void addCustomKp()}
                className="rounded border border-sky-300 px-2 py-1 text-xs text-sky-600 hover:bg-sky-50 disabled:opacity-40"
              >
                {t("curriculum.fields.customKpAdd")}
              </button>
            </div>
            <div className="mt-1 max-h-44 overflow-y-auto rounded border border-slate-200 p-2 flex flex-wrap gap-1.5">
              {leafKps.length === 0 && (
                <span className="text-xs text-slate-400">{t("curriculum.fields.noKps")}</span>
              )}
              {leafKps.map((k) => {
                const on = form.kp_ids.has(k.id);
                return (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => {
                      const next = new Set(form.kp_ids);
                      if (on) next.delete(k.id);
                      else next.add(k.id);
                      setForm({ ...form, kp_ids: next });
                    }}
                    className={`rounded border px-2 py-0.5 text-xs ${
                      on
                        ? "bg-sky-500 border-sky-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {k.name_zh}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-200 text-slate-600">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={!form.title.trim() || busy}
            onClick={save}
            className="px-3 py-1.5 text-sm rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
